import type { IHttpClient } from "@/core/interfaces";
import { isAPIError, readServiceError } from "@/core/services";
import { extractDetail, extractItems, isRecord, readText } from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  FornecedorConflitoRemanejado,
  FornecedorDisponivel,
  IgnorarConflitoFornecedorRequest,
  ListFornecedoresDisponiveisParams,
  RemanejarFornecedorRequest,
  RemanejarFornecedorResult,
} from "./recursos.types";

/** Contrato do hub-bff para resolver conflito de alocação de fornecedor (HU 2041). */
export const FORNECEDOR_CONFLITO_PATHS = {
  /** Fornecedores para escolher o substituto (privilégio `hub.equipe:view`). */
  FORNECEDORES: "/api/recursos/fornecedores",
  /** Ações de resolução do conflito (privilégio `hub.equipe:edit`). */
  REMANEJAMENTO: (alocacaoId: string) =>
    `/api/recursos/alocacoes/fornecedores/${encodeURIComponent(alocacaoId)}/remanejamento`,
  CONFLITOS_IGNORADOS: "/api/recursos/alocacoes/fornecedores/conflitos-ignorados",
} as const;

/** Código do 409 quando o substituto foi alocado entre a busca e a confirmação. */
export const FORNECEDOR_INDISPONIVEL_CODE = "FORNECEDOR_INDISPONIVEL";
export const FORNECEDOR_INDISPONIVEL_MESSAGE =
  "Este fornecedor ficou indisponível para o período. Selecione outro fornecedor.";

/** Erro tipado: o fornecedor escolhido ficou indisponível para o período. */
export class FornecedorIndisponivelError extends Error {
  constructor() {
    super(FORNECEDOR_INDISPONIVEL_MESSAGE);
    this.name = "FornecedorIndisponivelError";
  }
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function text(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return readText(value).trim();
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toFornecedorDisponivel(raw: unknown): FornecedorDisponivel | null {
  if (!isRecord(raw)) return null;
  const id = text(raw.id);
  const codigo = text(raw.codigo);
  const nome = text(raw.nome);
  if (!id && !codigo && !nome) return null;

  return {
    id: id || codigo,
    codigo,
    nome,
    disponibilidade:
      text(raw.disponibilidade).toUpperCase() === "DISPONIVEL" ? "DISPONIVEL" : "INDISPONIVEL",
  };
}

function toConflitoRemanejado(raw: unknown): FornecedorConflitoRemanejado | null {
  if (!isRecord(raw)) return null;
  return {
    fornecedorId: text(raw.fornecedorId),
    inicio: text(raw.inicio),
    fim: text(raw.fim),
    alocacaoIds: list(raw.alocacaoIds).map(text).filter(Boolean),
  };
}

function isFornecedorIndisponivel(error: unknown): boolean {
  return (
    isAPIError(error) &&
    error.status === 409 &&
    readServiceError(error).code === FORNECEDOR_INDISPONIVEL_CODE
  );
}

/** Fornecedores candidatos a substituto, com a disponibilidade no período da alocação. */
export async function listFornecedoresDisponiveis(
  request: ListFornecedoresDisponiveisParams,
): Promise<FornecedorDisponivel[]> {
  const params: Record<string, string> = {
    dataInicio: request.dataInicio,
    dataFim: request.dataFim,
  };
  if (request.ignorarObraId) params.ignorarObraId = request.ignorarObraId;
  const term = request.busca?.trim();
  if (term) params.busca = term;

  const response = await getHttpClient().get<unknown>(FORNECEDOR_CONFLITO_PATHS.FORNECEDORES, {
    signal: request.signal,
    params,
  });

  return extractItems(response.data, ["fornecedores"])
    .map(toFornecedorDisponivel)
    .filter((item): item is FornecedorDisponivel => item !== null);
}

/** Troca o fornecedor da alocação; 409 `FORNECEDOR_INDISPONIVEL` vira `FornecedorIndisponivelError`. */
export async function remanejarFornecedor(
  alocacaoId: string,
  body: RemanejarFornecedorRequest,
): Promise<RemanejarFornecedorResult> {
  try {
    const response = await getHttpClient().patch<unknown>(
      FORNECEDOR_CONFLITO_PATHS.REMANEJAMENTO(alocacaoId),
      body,
    );
    const data = extractDetail(response.data);
    const record = isRecord(data) ? data : {};
    const alocacao = isRecord(record.alocacao) ? record.alocacao : {};

    return {
      alocacao: {
        id: text(alocacao.id),
        obraId: text(alocacao.obraId),
        fornecedorId: text(alocacao.fornecedorId),
        inicio: text(alocacao.inicio),
        fim: text(alocacao.fim),
      },
      fornecedorAnteriorId: text(record.fornecedorAnteriorId),
      novoConflito: record.novoConflito === true,
      conflitos: list(record.conflitos)
        .map(toConflitoRemanejado)
        .filter((item): item is FornecedorConflitoRemanejado => item !== null),
    };
  } catch (error) {
    if (isFornecedorIndisponivel(error)) throw new FornecedorIndisponivelError();
    throw error;
  }
}

/** Marca o conflito como tratado (201); 409 quando já estava registrado. */
export async function ignorarConflitoFornecedor(
  body: IgnorarConflitoFornecedorRequest,
): Promise<void> {
  await getHttpClient().post<unknown>(FORNECEDOR_CONFLITO_PATHS.CONFLITOS_IGNORADOS, body);
}
